import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';

import { FormikTextField } from './utils/fields';

import { getReloadProductThunk, upsertCard } from 'common/features/store/duck/wallet/duck';
import {
    isPinRequiredForReloadOrBalance,
    registerCard,
    RegisterCardRequest,
} from 'common/api/wallet/cards';
import { useAlert } from 'modules/alerts';
import { LoadingOverlay, openLoader, closeLoader } from 'components/loading-overlay/LoadingOverlay';
import { getCardName } from 'common/api/utils/getCardName';
import { emptyUniversalBrand } from 'common/api/models/UniversalBrand';
import { getBrandThunk } from 'common/features/store/duck/brands/duck';
import { hasServerError } from 'common/api/utils/hasServerError';
import { useRootSelector } from 'rootStore';
import { createStyles } from 'utils/createStyle';
import { walletCardPinValidator } from 'common/modules/form-validation/validators';

interface RegisterCardDialogProps {
    brandId: number;
    supplierCode: string;
    children: React.ReactNode;
}

const styles = createStyles({
    content: ['relative', { width: '380px' }],
});

export const RegisterCardDialog = ({
    children,
    brandId,
    supplierCode,
}: RegisterCardDialogProps) => {
    const initialFormValues: RegisterCardRequest = {
        cardNumber: '',
        pin: '',
        productSku: '',
        brandId,
        name: '',
        supplierCode: '',
    };
    const [isRequired, setIsRequired] = useState(false);

    const { userId } = useRootSelector((s) => s.store.account);
    const dispatch = useDispatch<any>();

    const alert = useAlert();
    const loader = useRef<LoadingOverlay>(null);

    const doRegisterCard = useCallback(
        async (_registerCardRequest: RegisterCardRequest) => {
            if (!userId) {
                return;
            }

            openLoader(loader.current);

            try {
                const reload = await getReloadProductThunk(_registerCardRequest.brandId)(dispatch);

                if (reload == null || reload.data == null) {
                    return;
                }

                const [result, brandResult] = await Promise.all([
                    registerCard({
                        ..._registerCardRequest,
                        productSku: reload.data.sku,
                        supplierCode: reload.data.supplierCode,
                    }),
                    getBrandThunk(_registerCardRequest.brandId)(dispatch),
                ]);

                if (result.data) {
                    dispatch(upsertCard({ userId, card: result.data }));

                    const name = getCardName(result.data, brandResult.data || emptyUniversalBrand);

                    alert({
                        title: 'Card registered successfully!',
                        message: `You've successfully added ${name || 'the card'} to your Wallet`,
                    });

                    return;
                }

                if (!hasServerError(result)) {
                    const invalidCardNumberMessage = `Invalid Card Number
Please note, you can only add reloadable Gift Cards purchased from us. eGift Cards are automatically added.`;
                    alert({ title: 'Invalid Card Number', message: invalidCardNumberMessage });
                    return;
                }
            } finally {
                await closeLoader(loader.current);
            }

            return null;
        },
        [dispatch, userId, alert]
    );

    const onSubmit = async (request: RegisterCardRequest) => {
        await doRegisterCard(request);
    };

    const validateFormData = (formValues: RegisterCardRequest) => {
        const errors: { [key: string]: string } = {};

        if (!formValues.cardNumber) {
            errors.cardNumber = 'Card Number is required';
        }

        return errors;
    };

    useEffect(() => {
        isPinRequiredForReloadOrBalance(supplierCode).then((result) => {
            setIsRequired(result.data?.isPinRequired || false);
        });
    }, []);

    const validatePin = useCallback(
        (value: string) => {
            if (value.length) {
                return walletCardPinValidator(value);
            } else if (isRequired) {
                return 'Pin is required.';
            }
        },
        [isRequired]
    );

    return (
        <div className={styles.content}>
            <LoadingOverlay ref={loader} />
            <Formik
                initialValues={initialFormValues}
                onSubmit={onSubmit}
                validate={validateFormData}
            >
                {(formikProps) => (
                    <Form>
                        <div data-recording-disable>
                            <div className="mb-6">
                                <FormikTextField<RegisterCardRequest>
                                    name="cardNumber"
                                    title="Card Number"
                                    label="Card Number"
                                    placeholder="Card Number"
                                    error={formikProps.errors.cardNumber}
                                />
                            </div>
                            <div className="mb-6">
                                <FormikTextField<RegisterCardRequest>
                                    name="pin"
                                    title="PIN"
                                    label="PIN"
                                    placeholder="PIN"
                                    error={formikProps.errors.pin}
                                    validate={validatePin}
                                />
                            </div>
                        </div>
                        {children}
                    </Form>
                )}
            </Formik>
        </div>
    );
};
